import * as React from 'react';
import { InjectedTranslateProps, translate } from 'react-i18next';
import DismissSmall from 'wix-ui-icons-common/DismissSmall';

import { MembersTableState, SortDirection, TeamMemberTableRowData, MembersColumnTypes } from '../types';
import * as css from './TeamMembersList.scss';
import {
  getRenderActionsCell,
  renderStatusDate,
  renderNameCell,
  renderRoleCell,
  renderUserImageCell,
} from './TeamMembersListComponents';
import RolesFilter from './TeamMembersListRolesFilter';
import StatusFilter from './TeamMembersListStatusFilter';
import { InjectedExperimentsProps, withExperiments } from '@wix/wix-experiments-react';
import { Role } from '../../types/roles';
import { ConnectedProps } from 'react-redux';
import { connector } from '../../components/TeamMembersListPage/TeamMembersListContainer';
import { Card, Page, Table, TableToolbar, TextButton } from 'wix-style-react';
import { DataTableColumn } from 'wix-style-react/dist/es/src/Table/DataTable';
import { EnforceContributorsTableText } from '../../components/EnforceContributors/EnforceContributors';
import TeamMembersListNamesAndEmailsFilter from './TeamMembersListNamesAndEmailsFilter';

interface IProps extends InjectedTranslateProps, InjectedExperimentsProps, ConnectedProps<typeof connector> {
  data: TeamMemberTableRowData[];
  computedData: TeamMemberTableRowData[];
  header: any;
  membersTableState: MembersTableState;
  roleIds: string[];
  currentUserId: string;
  permissions: Set<string>;
  locale: string;
  roles: Role[];
  isOwner: boolean;
  siteLevel?: boolean;
  isAllowedToManageRoles: boolean;
  ownerId: string;
}

type DataTableColumnExtended = DataTableColumn & {key: string};

class TeamMembersList extends React.Component<IProps> {

  getDescedingValue = (key: string) => {
    const { membersTableState } = this.props;
    // It's a ternary since the output we expect should be: true for arrow up, false for down and undefined for nan
    return membersTableState.sort.column === key ? membersTableState.sort.direction === SortDirection.desc : undefined;
  }

  render() {
    const {
      t,
      header,
      data,
      computedData,
      onRemoveRequest,
      onGetLink,
      onResendRequest,
      onRoleFilterAdded,
      onRoleFilterRemoved,
      onStatusFilterChanged,
      onSortToggled,
      onClearFilters,
      membersTableState,
      roleIds,
      experiments,
      permissions,
      currentUserId,
      locale,
      roles,
      viewInvitesPermission,
      siteLevel,
      isOwner,
      isAllowedToManageRoles,
      ownerId,
      currentAccountId,
      onResendTransferOwnership,
      onCancelTransferOwnership,
      onGetTransferOwnershipLink,
      reportOpenChangeRolesBI,
      onNamesAndEmailsFilterChanged,
    } = this.props;
    const renderActionsCell = getRenderActionsCell({
      t,
      onRemoveRequest,
      currentUserId,
      onGetLink,
      onResendRequest,
      permissions,
      showOwnerAction: siteLevel && isOwner,
      siteLevel,
      currentAccountId,
      isUserOwner: isOwner,
      ownerId,
      onResendTransferOwnership,
      onCancelTransferOwnership,
      onGetTransferOwnershipLink,
      reportOpenChangeRolesBI,
    });

    const showClearFiltersButton = !!(membersTableState.filters.roles.length || membersTableState.filters.status || membersTableState.filters.namesAndEmails);

    if (experiments.enabled('specs.wos.AccountTeamsTableActions')) {
      return (
        <div className={css.wrapper}>
          <Card>
            <div className={css.tableWrapper}>
              <Table
                withWrapper
                data-hook="account-team-table"
                data={computedData}
                rowVerticalPadding="medium"
                rowClass={css.row}
                columns={computedData.length === 0 ? [] : [
                  { title: '', render: renderUserImageCell, width: '86px', style: { boxSizing: 'border-box' } },
                  { title: t('list.name'), render: renderNameCell(permissions, locale), width: '300px', sortable: true, sortDescending: this.getDescedingValue(MembersColumnTypes.NAME), key: MembersColumnTypes.NAME } as DataTableColumnExtended,
                  { title: t('list.role'), render: renderRoleCell(roles, t), width: '186px', sortable: true, sortDescending: this.getDescedingValue(MembersColumnTypes.ROLE), key: MembersColumnTypes.ROLE } as DataTableColumnExtended,
                  { title: t('list.joinedTeamAt'), render: renderStatusDate(t, locale), sortable: true, sortDescending: this.getDescedingValue(MembersColumnTypes.DATE), key: MembersColumnTypes.DATE } as DataTableColumnExtended,
                  { title: '', render: renderActionsCell, width: '158px', align: 'end' }
                ]}
                onSortClick={(column: DataTableColumnExtended) => onSortToggled(column.key)}
              >
                <Page.Sticky>
                  <>
                    <Card>
                      {header}
                    </Card>
                    <Card>
                      <TableToolbar>
                        <TableToolbar.ItemGroup position="start">
                          {viewInvitesPermission &&
                          <TableToolbar.Item>
                            <StatusFilter
                              data={data}
                              membersTable={membersTableState}
                              onStatusFilterChanged={onStatusFilterChanged}
                            />
                          </TableToolbar.Item>}
                          <TableToolbar.Item>
                            <RolesFilter
                              data={data}
                              roles={roles}
                              onRoleFilterAdded={onRoleFilterAdded}
                              onRoleFilterRemoved={onRoleFilterRemoved}
                              membersTable={membersTableState}
                              isAllowedToManageRoles={isAllowedToManageRoles}
                              roleIds={roleIds}
                            />
                          </TableToolbar.Item>
                          <TableToolbar.Item>
                            <TeamMembersListNamesAndEmailsFilter
                              membersTable={membersTableState}
                              onNamesAndEmailsFilterChanged={onNamesAndEmailsFilterChanged}/>
                          </TableToolbar.Item>
                        </TableToolbar.ItemGroup>
                        <TableToolbar.ItemGroup position="end">
                          {showClearFiltersButton &&
                          <TableToolbar.Item>
                            <TextButton data-hook="team-members-list-clear-filters" onClick={onClearFilters} prefixIcon={<DismissSmall />} size="small" weight="normal">
                              {t('list.filters.clear')}
                            </TextButton>
                          </TableToolbar.Item>
                          }
                        </TableToolbar.ItemGroup>
                      </TableToolbar>
                      <Table.Titlebar/>
                    </Card>
                  </>
                </Page.Sticky>
                <Table.Content titleBarVisible={false} />
                {computedData.length === 0 &&
                  <Table.EmptyState
                    dataHook="account-team-table-empty-state"
                    title={t(siteLevel ? 'list.emptyState.site.title' : 'list.emptyState.team.title')}
                    subtitle={
                      <div>
                        {`${t('list.emptyState.subtitle')} `}
                        <TextButton onClick={onClearFilters}>
                          {t('list.emptyState.clear')}
                        </TextButton>
                        </div>
                    }
                    theme="page-no-border"
                  >
                  </Table.EmptyState>
                }
              </Table>
            </div>
          </Card>
          <EnforceContributorsTableText t={t}/>
        </div>
      );
    }

    return (
      <div className={css.wrapper}>
        <Card>
          <div className={css.tableWrapper}>
            <Table
              withWrapper
              data-hook="account-team-table"
              data={computedData}
              rowVerticalPadding="medium"
              rowClass={css.row}
              columns={[
                { title: '', render: renderUserImageCell, width: '86px', style: { boxSizing: 'border-box' } },
                { title: t('list.name'), render: renderNameCell(permissions, locale), width: '300px' },
                { title: t('list.role'), render: renderRoleCell(roles, t), width: '186px' },
                { title: t('list.joinedTeamAt'), render: renderStatusDate(t, locale) },
                { title: '', render: renderActionsCell, width: '32px' }
              ]}
              onSortClick={column => onSortToggled(`${column.title}`)}
            >
              <Page.Sticky>
                <>
                  <Card>
                    {header}
                    <Table.Titlebar/>
                  </Card>
                </>
              </Page.Sticky>
              <Table.Content titleBarVisible={false} />
              {computedData.length === 0 &&
                <Table.EmptyState
                  dataHook="account-team-table-empty-state"
                  title={t('list.emptyState.title')}
                  subtitle={t('list.emptyState.subtitle')}
                >
                  <TextButton onClick={onClearFilters}>
                    {t('list.emptyState.clear')}
                  </TextButton>
                </Table.EmptyState>
              }
            </Table>
          </div>
        </Card>
      </div>
    );
  }
}

export default withExperiments(translate()(TeamMembersList));
